/**
 * Shared loading / error / empty states for the list screens.
 *
 * The error state deliberately says nothing about WHY beyond what the rail told us. The admin
 * rail's errors are coarse on purpose (a 403 on break-glass is byte-identical for "not in
 * needs_review" and "no such request", so probing ids reveals nothing) — inventing a friendlier
 * explanation here would be inventing information the server refused to give.
 */
import type { ReactNode } from 'react';
import { ThreadMark } from './ThreadMark';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';

/**
 * Inline error text.
 *
 * The message is `ink`, and the coral is a RULE down the left rather than the text colour.
 * `coral-deep` is the house error colour but it measures 4.03:1 on paper / 4.33:1 on surface —
 * under AA for 12px text — so colouring the words with it makes the error the least readable
 * thing on screen, which is backwards. The coral rule carries "this is a problem" visually,
 * `role="alert"` carries it to assistive tech, and the words stay at 15:1.
 *
 * Interim: WP-F14 has no `--coral-text` to reach for. Task #57 (founder-ratified) is adding
 * text-safe tokens to `packages/tokens`; when it lands, this can become that token.
 */
export function ErrorText({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p
      role="alert"
      className={cn(
        'border-l-2 border-coral-deep pl-2.5 font-body text-[12px] font-medium leading-relaxed text-ink',
        className,
      )}
    >
      {children}
    </p>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-line bg-surface px-6 py-14 text-center">
      {/* "Never a dead spinner": a gentle brand pulse (CSS; stilled under reduced motion). */}
      <div className="brand-pulse">
        <ThreadMark className="h-5 w-auto" />
      </div>
      <p className="mt-3 font-display text-[12.5px] font-medium text-ink-70" role="status">
        {label}
      </p>
    </div>
  );
}

export function ErrorState({ onRetry, message }: { onRetry?: () => void; message?: string }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-coral-tint bg-coral-tint/40 px-6 py-10 text-center"
    >
      <h2 className="font-display text-[15px] font-semibold text-ink">Couldn&rsquo;t load that</h2>
      <p className="mx-auto mt-1.5 max-w-[46ch] font-body text-[12.5px] leading-relaxed text-ink-70">
        {message ?? 'Something went wrong on our side. Try again in a moment.'}
      </p>
      {onRetry && (
        <div className="mt-4">
          <Button variant="ghost" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-lg border border-line bg-surface px-6 py-14 text-center">
      <h2 className="font-display text-[15px] font-semibold text-ink">{title}</h2>
      {children && (
        <p className="mx-auto mt-1.5 max-w-[46ch] font-body text-[12.5px] leading-relaxed text-ink-70">
          {children}
        </p>
      )}
    </div>
  );
}
